import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import styled, { useTheme } from 'styled-components'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import { useTranslation } from 'react-i18next'
import { ethers } from 'ethers'
import { Contract } from '@ethersproject/contracts'
import type { MarketplaceListing } from '@mprolab/sdk'
import MainWrapper from 'layouts/MainWrapper'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { readListing } from 'utils/marketplace/readListings'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { useWeb3ProviderAndContract } from 'hooks/useWeb3ProviderAndContract'
import { ERC20_MIN_ABI, ERC721_MIN_ABI, MARKETPLACE_ABI } from 'utils/marketplace/abi'
import { decodeMarketplaceError } from 'utils/marketplace/errorMessages'
import { reflectLicenceTransfer } from 'utils/marketplace/reflectLicenceTransfer'
import { invalidateLicenceLists } from 'state/services/indexNode/indexNode'
import { useUnclaimedForToken } from 'utils/marketplace/useUnclaimedForToken'
import TxConfirmation from 'components/TxConfirmation'
import useToast from 'hooks/useToast'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'

const Section = styled(Flex)`
  flex-direction: column;
  gap: 12px;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
`

const InlineInput = styled.input`
  flex: 1 1 200px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.cyan};
  }
`

const ListingDetailView: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>()
  const { t } = useTranslation()
  const { colors } = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { account, provider } = useWeb3React()
  const ctx = useMarketplaceContext()
  /**
   * Every signing action here must run on Base.
   *
   * Nothing in the marketplace enforced the chain, so an approval signed
   * while the wallet sat on Ethereum went to an address that holds no
   * contract there — MetaMask showed 'no changes' and the listing could
   * never proceed. Every other contract flow in the app already does this.
   */
  const ensureChainForTx = useSwitchChainForContractTx()
  /**
   * A signer bound to Base, not to whatever chain the wallet is sitting on.
   *
   * Switching the wallet is not enough: the provider captured in this
   * render still points at the old chain, so the transaction went out on
   * Ethereum even after a successful switch. Every other contract flow in
   * the app resolves its signer by chain id for exactly this reason.
   */
  const { getProviderOrSignerByChainId } = useWeb3ProviderAndContract()
  const { toastSuccess, toastError } = useToast()

  const [listing, setListing] = useState<MarketplaceListing | null>(null)
  const [loading, setLoading] = useState(true)
  // Every action here costs gas, and each one ends by showing its hash: a
  // toast disappears, a transaction is the user's own record of what happened.
  const [txHash, setTxHash] = useState<string | null>(null)
  /**
   * What this listing pays out under the terms it was created with. The fee is
   * frozen into the listing, so the current rate can differ — the seller is
   * owed what they agreed to, and the page should say which that is.
   */
  const [listingQuote, setListingQuote] = useState<{
    fee: string
    proceeds: string
  } | null>(null)
  const [txTitle, setTxTitle] = useState<string>('')
  const [busy, setBusy] = useState<'buy' | 'cancel' | 'update' | 'approve' | 'clear' | null>(null)
  const [allowance, setAllowance] = useState<bigint | null>(null)
  const [newPrice, setNewPrice] = useState('')
  /** Result of pre-flight checks against the NFT contract. `null` means
   *  "not yet known" so we render a non-blocking placeholder until the
   *  read returns. */
  const [chainOwner, setChainOwner] = useState<string | null>(null)
  const [sellerHasApproval, setSellerHasApproval] = useState<boolean | null>(null)

  const refetch = useCallback(async () => {
    if (!listingId || !ctx.marketplaceAddress) return
    setLoading(true)
    try {
      const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
      const l = await readListing({
        marketplaceAddress: ctx.marketplaceAddress,
        provider: rpc,
        listingId
      })
      setListing(l)
    } finally {
      setLoading(false)
    }
  }, [listingId, ctx.marketplaceAddress, ctx.chainId])

  useEffect(() => {
    void refetch()
  }, [refetch])

  // Pre-flight: read current NFT owner + seller's approval state. This
  // is what surfaces a friendly "seller no longer owns this token"
  // banner before the buyer wastes gas on a doomed buy().
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!ctx.marketplaceAddress || !listing) {
        setChainOwner(null)
        setSellerHasApproval(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
        const erc721 = new Contract(listing.nft, ERC721_MIN_ABI as unknown as ethers.ContractInterface, rpc)
        const [owner, approvedToken, approvedAll] = await Promise.all([
          erc721.ownerOf(listing.tokenId).catch(() => ethers.constants.AddressZero),
          erc721.getApproved(listing.tokenId).catch(() => ethers.constants.AddressZero),
          erc721.isApprovedForAll(listing.seller, ctx.marketplaceAddress).catch(() => false)
        ])
        if (cancelled) return
        setChainOwner(String(owner).toLowerCase())
        setSellerHasApproval(
          Boolean(approvedAll) || String(approvedToken).toLowerCase() === ctx.marketplaceAddress.toLowerCase()
        )
      } catch {
        if (!cancelled) {
          setChainOwner(null)
          setSellerHasApproval(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [ctx.marketplaceAddress, ctx.chainId, listing])

  // Fetch buyer's MPRO allowance whenever the listing or wallet changes.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!account || !ctx.mproAddress || !ctx.marketplaceAddress || !listing) {
        setAllowance(null)
        return
      }
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
        const mpro = new Contract(ctx.mproAddress, ERC20_MIN_ABI as unknown as ethers.ContractInterface, rpc)
        const a: ethers.BigNumber = await mpro.allowance(account, ctx.marketplaceAddress)
        if (!cancelled) setAllowance(a.toBigInt())
      } catch {
        if (!cancelled) setAllowance(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [account, ctx.mproAddress, ctx.marketplaceAddress, ctx.chainId, listing])

  const collectionLabel = useMemo(() => {
    if (!listing) return ''
    return (
      ctx.collections.find((c) => c.nft === listing.nft)?.label ?? `${listing.nft.slice(0, 6)}…${listing.nft.slice(-4)}`
    )
  }, [ctx.collections, listing])

  // What this listing actually pays out, from the contract, under the terms
  // frozen into it when it was created.
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      if (!ctx.marketplaceAddress || !listing?.listingId) return
      try {
        const rpc = getSimpleRpcProviderByChainId(ctx.chainId) as ethers.providers.Provider
        const market = new Contract(ctx.marketplaceAddress, MARKETPLACE_ABI as unknown as ethers.ContractInterface, rpc)
        const [fee, proceeds] = await market.quoteListing(listing.listingId)
        if (cancelled) return
        setListingQuote({
          fee: ethers.utils.formatEther(fee),
          proceeds: ethers.utils.formatEther(proceeds)
        })
      } catch {
        if (!cancelled) setListingQuote(null)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [ctx.marketplaceAddress, ctx.chainId, listing?.listingId])

  // Stated by the platform so the buyer need not take the seller's word.
  const unclaimed = useUnclaimedForToken(listing?.nft, listing ? String(listing.tokenId) : undefined)

  const priceMpro = useMemo(
    () =>
      listing
        ? (() => {
            try {
              return ethers.utils.formatEther(listing.priceWei)
            } catch {
              return '?'
            }
          })()
        : '?',
    [listing]
  )

  const isSeller = !!account && !!listing && account.toLowerCase() === listing.seller

  const needsApproval = !!listing && allowance !== null && allowance < BigInt(listing.priceWei)

  /**
   * "Stale" = listing.active=true on chain, but the seller no longer
   * owns the token, or has revoked the marketplace's approval. In both
   * cases buy() would revert; the UI must block the buyer and prompt
   * the seller to cancel.
   */
  const stale = useMemo(() => {
    if (!listing || chainOwner === null || sellerHasApproval === null) {
      return null
    }
    if (chainOwner !== listing.seller) {
      return 'owner-changed' as const
    }
    if (!sellerHasApproval) {
      return 'approval-revoked' as const
    }
    return null
  }, [listing, chainOwner, sellerHasApproval])

  const onApproveMpro = useCallback(async () => {
    if (!provider || !account || !ctx.mproAddress || !ctx.marketplaceAddress || !listing) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('approve')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const mpro = new Contract(ctx.mproAddress, ERC20_MIN_ABI as unknown as ethers.ContractInterface, signer)
      const tx = await mpro.approve(ctx.marketplaceAddress, listing.priceWei)
      await tx.wait()
      toastSuccess(t('marketplace.approveMproOk', { price: priceMpro }))
      // Trigger allowance re-read.
      setAllowance(BigInt(listing.priceWei))
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [dispatch, provider, account, ctx.mproAddress, ctx.marketplaceAddress, listing, toastError, toastSuccess, t])

  const onBuy = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress || !listing) return

    // Pre-flight ownership + approval check so the buyer doesn't waste
    // gas on a doomed tx and gets a clear human-readable message.
    if (stale === 'owner-changed') {
      toastError(t('marketplace.errors.sellerNoLongerOwnsToken'))
      return
    }
    if (stale === 'approval-revoked') {
      toastError(t('marketplace.errors.missingNftApproval'))
      return
    }

    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('buy')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      // The price the buyer is looking at is the ceiling they agree to. If
      // the seller raises it between render and confirmation, the contract
      // refuses the trade instead of charging whatever allowance exists.
      const tx = await market.buy(listing.listingId, listing.priceWei)
      setTxHash(tx.hash)
      setTxTitle(t('marketplace.boughtTitle'))
      await tx.wait()
      // The buyer is about to land on their licence list; without reflecting
      // the movement first, that list is served from a registry the transfer
      // indexer has not caught up with yet, and the licence they just paid
      // for is nowhere on it.
      await reflectLicenceTransfer(listing.nft, String(listing.tokenId))
      // The buyer is being sent to a licence list their browser cached before
      // they owned anything. Clearing the registry's cache is not enough —
      // this is the copy they would actually be shown.
      dispatch(invalidateLicenceLists())
      toastSuccess(
        t('marketplace.buyOkDetailed', {
          price: priceMpro
        })
      )
      await refetch()
      navigate('/user-licenses')
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    ctx.marketplaceAddress,
    listing,
    stale,
    priceMpro,
    refetch,
    navigate,
    toastError,
    toastSuccess,
    t
  ])

  /**
   * Retires a listing that can no longer settle — the seller parted with the
   * token, or revoked the approval. Callable by anyone, because the person it
   * unblocks is the current owner, who cannot cancel someone else's listing
   * and cannot list the licence while this one stands.
   */
  const onClearStale = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress || !listing) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('clear')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const tx = await market.clearStale(listing.listingId)
      setTxHash(tx.hash)
      setTxTitle(t('marketplace.staleClearedTitle'))
      await tx.wait()
      await refetch()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [
    provider,
    account,
    ctx.marketplaceAddress,
    ctx.chainId,
    listing,
    ensureChainForTx,
    getProviderOrSignerByChainId,
    refetch,
    toastError,
    t
  ])

  const onCancel = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress || !listing) return
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('cancel')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const tx = await market.cancel(listing.listingId)
      setTxHash(tx.hash)
      setTxTitle(t('marketplace.cancelledTitle'))
      await tx.wait()
      toastSuccess(t('marketplace.cancelOkDetailed'))
      await refetch()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [provider, account, ctx.marketplaceAddress, listing, refetch, toastError, toastSuccess, t])

  const onUpdatePrice = useCallback(async () => {
    if (!provider || !account || !ctx.marketplaceAddress || !listing) return
    let parsed: ethers.BigNumber
    try {
      parsed = ethers.utils.parseEther(newPrice.trim() || '0')
    } catch {
      toastError(t('marketplace.invalidPrice'))
      return
    }
    if (parsed.lte(0)) {
      toastError(t('marketplace.invalidPrice'))
      return
    }
    if (!(await ensureChainForTx(ctx.chainId))) return
    setBusy('update')
    try {
      const signer = getProviderOrSignerByChainId(ctx.chainId, account)
      const market = new Contract(
        ctx.marketplaceAddress,
        MARKETPLACE_ABI as unknown as ethers.ContractInterface,
        signer
      )
      const tx = await market.updatePrice(listing.listingId, parsed)
      setTxHash(tx.hash)
      setTxTitle(t('marketplace.priceUpdatedTitle'))
      await tx.wait()
      toastSuccess(
        t('marketplace.updatePriceOkDetailed', {
          price: ethers.utils.formatEther(parsed)
        })
      )
      setNewPrice('')
      await refetch()
    } catch (e) {
      toastError(decodeMarketplaceError(e, t))
    } finally {
      setBusy(null)
    }
  }, [provider, account, ctx.marketplaceAddress, listing, newPrice, refetch, toastError, toastSuccess, t])

  if (loading) {
    return (
      <MainWrapper>
        <Flex fullWidth justifyContent="center" py="80px">
          <Text color={colors.gray6}>{t('marketplace.loading')}</Text>
        </Flex>
      </MainWrapper>
    )
  }

  if (!listing) {
    return (
      <MainWrapper>
        <Flex fullWidth flexDirection="column" mt="152px" mb="120px" gap="16px">
          <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace')}>
            ← {t('marketplace.backToList')}
          </Button>
          <Text variant={TextVariant.BODY_DEFAULT}>{t('marketplace.notFound')}</Text>
        </Flex>
      </MainWrapper>
    )
  }

  return (
    <MainWrapper>
      <Flex fullWidth flexDirection="column" mt="152px" mb="120px" gap="20px">
        {txHash ? <TxConfirmation hash={txHash} chainId={ctx.chainId} title={txTitle} /> : null}
        <Button type="button" variant="secondary" size="sm" onClick={() => navigate('/marketplace')}>
          ← {t('marketplace.backToList')}
        </Button>

        <Section>
          <Flex justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap="12px">
            <Text variant={TextVariant.H4}>
              {collectionLabel} #{listing.tokenId}
            </Text>
            <Text variant={TextVariant.H4} color={colors.cyan}>
              {priceMpro} MPRO
            </Text>
          </Flex>
          {unclaimed.unclaimedMpro !== null && unclaimed.unclaimedMpro > 0 && (
            <Text variant={TextVariant.BODY_SMALL} color={colors.green}>
              {t('marketplace.carriesUnclaimedDetail', {
                amount: unclaimed.unclaimedMpro.toLocaleString('en-US', {
                  maximumFractionDigits: 2
                })
              })}
            </Text>
          )}
          {listingQuote ? (
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {isSeller
                ? t('marketplace.youReceive', { amount: listingQuote.proceeds })
                : t('marketplace.sellerReceives', {
                    amount: listingQuote.proceeds
                  })}
              {' · '}
              {t('marketplace.platformFeeShort', { amount: listingQuote.fee })}
            </Text>
          ) : null}
          <Flex gap="14px" flexWrap="wrap">
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('marketplace.seller')}: {listing.seller}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('marketplace.contract')}: {listing.nft}
            </Text>
          </Flex>
          <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
            {!listing.active ? t('marketplace.inactive') : t('marketplace.activeListing')}
          </Text>
        </Section>

        {listing.active && stale ? (
          <Section
            style={{
              borderColor: colors.red,
              background: `${colors.red}11`
            }}
          >
            <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.red}>
              {stale === 'owner-changed' ? t('marketplace.staleOwnerTitle') : t('marketplace.staleApprovalTitle')}
            </Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {stale === 'owner-changed' ? t('marketplace.staleOwnerExplain') : t('marketplace.staleApprovalExplain')}
            </Text>
            <Flex gap="10px" flexWrap="wrap">
              {isSeller ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={busy !== null}
                  onClick={() => void onCancel()}
                >
                  {busy === 'cancel' ? t('marketplace.cancelling') : t('marketplace.cancelStaleListing')}
                </Button>
              ) : null}
              {/*
                Anyone may retire a listing that can no longer settle, and the
                person it helps most is the current owner — who cannot cancel
                it, because only the recorded seller can, and who cannot list
                the licence at all while it stands.
              */}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={busy !== null}
                onClick={() => void onClearStale()}
              >
                {busy === 'clear' ? t('marketplace.clearingStale') : t('marketplace.clearStaleListing')}
              </Button>
            </Flex>
          </Section>
        ) : null}

        {listing.active && !isSeller && !stale ? (
          <Section>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.buyTitle')}</Text>
            <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
              {t('marketplace.buyExplain', { price: priceMpro })}
            </Text>
            {!account ? (
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('marketplace.connectFirst')}
              </Text>
            ) : needsApproval ? (
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={busy !== null}
                onClick={() => void onApproveMpro()}
              >
                {busy === 'approve' ? t('marketplace.approving') : t('marketplace.approveMpro', { price: priceMpro })}
              </Button>
            ) : (
              <Button type="button" variant="primary" size="md" disabled={busy !== null} onClick={() => void onBuy()}>
                {busy === 'buy' ? t('marketplace.buying') : t('marketplace.buyNow', { price: priceMpro })}
              </Button>
            )}
            <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
              {t('marketplace.atomicHint')}
            </Text>
          </Section>
        ) : null}

        {listing.active && isSeller ? (
          <Section>
            <Text variant={TextVariant.BODY_DEFAULT_BOLD}>{t('marketplace.sellerActions')}</Text>
            <Flex gap="10px" flexWrap="wrap" alignItems="center">
              <InlineInput
                placeholder={t('marketplace.newPricePlaceholder')}
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                inputMode="decimal"
              />
              <Button
                type="button"
                variant="secondary"
                size="md"
                disabled={busy !== null || !newPrice.trim()}
                onClick={() => void onUpdatePrice()}
              >
                {busy === 'update' ? t('marketplace.updating') : t('marketplace.updatePrice')}
              </Button>
            </Flex>
            <Button
              type="button"
              variant="secondary"
              size="md"
              disabled={busy !== null}
              onClick={() => void onCancel()}
            >
              {busy === 'cancel' ? t('marketplace.cancelling') : t('marketplace.cancelListing')}
            </Button>
          </Section>
        ) : null}
      </Flex>
    </MainWrapper>
  )
}

export default ListingDetailView
